import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { DuplicateTemplateModal } from '../../../admin/containers/templates/SurveyTemplates/DuplicateTemplateModal';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../models';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useCreateCustomSurveyFromTemplateMutation } from '../../../queries/Surveys/useCreateCustomSurveyFromTemplateMutation';
import { useSurveyTemplateQuery } from '../../../queries/SurveyTemplate';
import { LoadingLogo } from '../../components/LoadingLogo';
import { CustomSurvey } from '../../containers/SurveyCreation/CustomSurvey';
import { bs } from '../../styles/styles';

type IProps = IImpersonationContextComponentProps;

/**
 * Customer-facing page for previewing survey templates before cloning.
 *
 * Users can:
 * 1. View the template's questions and structure (read-only)
 * 2. Click Clone to create their own copy
 * 3. After cloning, navigate to edit mode of the new survey
 */
const PreviewSurveyTemplateBase: React.FC<IProps> = observer(({ impersonationContext }) => {
	const { id } = useParams<{ id: string }>();
	const history = useHistory();
	const userSession = useUserSession();

	/**
	 * Determines the correct impersonation context for API calls.
	 *
	 * Customer web app impersonation flow:
	 * - Admin impersonating customer: userSession contains impersonation info directly
	 * - Customer using normally: impersonationContext is undefined
	 * - This differs from admin portal which uses MobX Provider for impersonation
	 *
	 * Priority order:
	 * 1. If userSession indicates customer impersonation, build context from userSession
	 * 2. Otherwise use impersonationContext from props (may be undefined for normal users)
	 *
	 * This ensures template queries and mutations execute in correct user context.
	 */
	const effectiveImpersonationContext = React.useMemo((): Api.IImpersonationContext | undefined => {
		if (userSession?.isCustomerImpersonation && userSession.account) {
			return {
				account: userSession.account.toJs(),
				user: userSession.user,
			};
		}
		return impersonationContext;
	}, [userSession?.isCustomerImpersonation, userSession.account, userSession.user, impersonationContext]);
	const [isCloneModalOpen, setIsCloneModalOpen] = React.useState(false);

	const {
		data: templateData,
		isLoading,
		error,
	} = useSurveyTemplateQuery({
		id,
		enabled: !!id,
	});

	const createFromTemplateMutation = useCreateCustomSurveyFromTemplateMutation({
		impersonationContext: effectiveImpersonationContext,
		onSuccess: newSurvey => {
			toast.success('Survey created from template');
			setIsCloneModalOpen(false);
			// Navigate to edit mode of the newly created survey
			history.push(`/surveys/custom/edit/${newSurvey.id}`);
		},
		onError: mutationError => {
			toast.error('Failed to create survey from template');
			console.error('Create survey from template error:', mutationError);
		},
	});

	const handleCloneClick = () => {
		setIsCloneModalOpen(true);
	};

	const handleDuplicateConfirm = (newName: string) => {
		if (!templateData) return;

		createFromTemplateMutation.mutate({
			templateId: id,
			name: newName,
		});
	};

	if (isLoading) {
		return (
			<div className={css(bs.flexCenter, bs.p4)}>
				<LoadingLogo size={100} />
			</div>
		);
	}

	if (error || !templateData) {
		return (
			<div className={css(bs.p4)}>
				<p>Error loading template. Please try again later.</p>
			</div>
		);
	}

	return (
		<>
			<CustomSurvey
				templateMode={true}
				isEditingTemplate={false}
				isPreviewOnly={true}
				templateId={id}
				onSaveAsTemplate={handleCloneClick}
				initialSurveyDefinition={templateData.surveyDefinition}
				// Don't pass impersonationContext - CustomSurvey doesn't need it for preview mode
				// The mutation already uses effectiveImpersonationContext
			/>

			{templateData && (
				<DuplicateTemplateModal
					title='Clone Survey Template?'
					labelText='Survey name'
					open={isCloneModalOpen}
					onOpenChange={isOpen => setIsCloneModalOpen(isOpen)}
					templateName={templateData.name}
					onConfirm={handleDuplicateConfirm}
					isLoading={createFromTemplateMutation.isPending}
				/>
			)}
		</>
	);
});

export const PreviewSurveyTemplate = inject(ImpersonationContextKey)(PreviewSurveyTemplateBase);
